import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import ManageMetricsDialog from 'components/@home/@settings/Tabs/Agents/ManageMetricsDialog';
import ChartLineIcon from 'mdi-react/ChartLineIcon';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import RefreshIcon from 'mdi-react/RefreshIcon';
import { arrayOf, func, object } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { I18n } from 'utils/i18n';

const MetricsPanel = ({ agent, allMetrics = [], onSaveAgent, onReloadMetrics = null }) => {
  const navigate = useNavigate();
  const { flow, authCompany } = useAgentEdit();
  const [search, setSearch] = useState('');
  const [manageOpen, setManageOpen] = useState(false);

  const refresh = () => {
    if (onReloadMetrics) onReloadMetrics();
  };

  const attachedIds = useMemo(() => (agent?.metricIds || []).map(String), [agent?.metricIds]);

  const attachedMetrics = useMemo(() => {
    const idSet = new Set(attachedIds);
    return (allMetrics || []).filter(m => idSet.has(String(m._id)));
  }, [allMetrics, attachedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? attachedMetrics.filter(
          m =>
            (m.name || '').toLowerCase().includes(q) ||
            (m.description || '').toLowerCase().includes(q),
        )
      : attachedMetrics;
    return [...base].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [attachedMetrics, search]);

  const persistMetricIds = nextIds => onSaveAgent({ metricIds: nextIds });

  const openMetric = id => {
    if (!flow?._id) return;
    navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'metrics', metricId: id }));
  };

  const openCreate = () => openMetric('new');
  const openEdit = m => openMetric(m._id);

  const renderTable = () => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'background.paper',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{I18n.t('AgenticChatFlow.MetricsColumnName')}</TableCell>
            <TableCell>{I18n.t('AgenticChatFlow.MetricsColumnDescription')}</TableCell>
            <TableCell>{I18n.t('AgenticChatFlow.MetricsColumnRules')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map(m => (
            <TableRow key={m._id} sx={{ cursor: 'pointer' }} hover onClick={() => openEdit(m)}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChartLineIcon size={14} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {m.name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{m.description || '—'}</TableCell>
              <TableCell>{(m.rules || []).length}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const renderEmpty = () => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        color: 'text.secondary',
        p: 6,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          bgcolor: 'background.default',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ChartLineIcon size={26} />
      </Box>
      <Typography variant="body2">{I18n.t('AgenticChatFlow.MetricsEmptyHint')}</Typography>
      <Button
        variant="contained"
        color="primary"
        disableElevation
        startIcon={<PlusIcon size={16} />}
        onClick={openCreate}
      >
        {I18n.t('AgenticChatFlow.MetricsNew')}
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        px: 4,
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(22), fontWeight: 700 }}>
          {I18n.t('AgenticChatFlow.MetricsAllTitle')}
        </Typography>
        <Tooltip title={I18n.t('AgenticChatFlow.MetricsRefresh')}>
          <IconButton size="small" onClick={refresh}>
            <RefreshIcon size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<CogOutlineIcon size={16} />}
          onClick={() => setManageOpen(true)}
        >
          {I18n.t('AgenticChatFlow.MetricsManage')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disableElevation
          startIcon={<PlusIcon size={16} />}
          onClick={openCreate}
        >
          {I18n.t('AgenticChatFlow.MetricsNew')}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <TextField
            sx={{ minWidth: 220 }}
            size="small"
            placeholder={I18n.t('AgenticChatFlow.MetricsSearchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyIcon size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Box>

      {attachedMetrics.length === 0 ? renderEmpty() : renderTable()}

      <ManageMetricsDialog
        open={manageOpen}
        metrics={allMetrics}
        attachedIds={attachedIds}
        onClose={() => setManageOpen(false)}
        onSaveAttachments={persistMetricIds}
        onCreateMetric={() => {
          setManageOpen(false);
          openCreate();
        }}
      />
    </Box>
  );
};

MetricsPanel.propTypes = {
  agent: object.isRequired,
  allMetrics: arrayOf(object),
  tags: arrayOf(object),
  onSaveAgent: func.isRequired,
  onReloadMetrics: func,
};

export default MetricsPanel;
